import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { NgFor, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { USER_PERMISSIONS } from '../../app.constants';
import { Offcanvas } from 'bootstrap';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-enquiry-view',
  imports: [BreadcrumbComponent, NgIf, NgFor, ReactiveFormsModule, CKEditorModule, DatePipe, RouterLink],
  templateUrl: './enquiry-view.component.html',
  styleUrl: './enquiry-view.component.css'
})
export class EnquiryViewComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Enquiries',
    currentURL: 'View Enquiry',
    breadcrumb_url: '/admin/enquiries'
  };

  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  enquiryId: string = '';
  enquiryDetails: any = null;
  commentForm!: FormGroup;
  drawerOpen: boolean = false;
  Editor: any = ClassicEditor;

  @ViewChild('addCommentDrawer') offcanvas!: ElementRef;
  private offcanvasInstance: Offcanvas | null = null;

  // Comments data
  comments: any[] = [];

  /** Filter state handed over by the list page, replayed on Back/Edit. */
  private listFilters: Params = {};

  private routeSubscription?: Subscription;
  private queryParamsSubscription?: Subscription;
  private getEnquiryDetailsSubscription?: Subscription;
  private getCommentsSubscription?: Subscription;
  private saveCommentSubscription?: Subscription;

  constructor(private fb: FormBuilder) {
    this.commentForm = this.fb.group({
      comment: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.queryParamsSubscription = this.route.queryParams.subscribe((params) => {
      this.listFilters = params;
    });
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      this.enquiryId = params.get('id') ?? '';
      if (this.enquiryId) {
        this.getEnquiryDetails();
        if (this.checkViewCommentPermission) {
          this.getComments();
        }
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const offcanvasElement = this.offcanvas?.nativeElement;
      if (offcanvasElement) {
        this.offcanvasInstance = Offcanvas.getOrCreateInstance(offcanvasElement, {
          backdrop: 'static',
          keyboard: false,
        });
      }
    });
  }

  getEnquiryDetails() {
    this.getEnquiryDetailsSubscription = this.httpService.getEnquiryDetailsById(this.enquiryId).subscribe({
      next: (response) => {
        this.enquiryDetails = response.data;
      }
    });
  }

  getComments() {
    this.getCommentsSubscription = this.httpService.getEnquiryComments(this.enquiryId).subscribe({
      next: (response) => {
        this.comments = response.data;
      }
    });
  }

  backToEnquiries() {
    this.router.navigate(['admin', 'enquiries'], { queryParams: this.listFilters });
  }

  navigateToProperty(propertyId: string) {
    this.router.navigate(['admin', 'properties', 'property-view', propertyId]);
  }

  openCommentDrawer() {
    this.drawerOpen = true;
    if (this.offcanvasInstance) {
      this.offcanvasInstance.show();
    }
  }

  closeCommentDrawer() {
    this.drawerOpen = false;
    this.commentForm.reset();
    if (this.offcanvasInstance) {
      this.offcanvasInstance.hide();
    }
  }

  saveComment() {
    if (this.commentForm.valid) {
      const commentData = {
        comment: this.commentForm.get('comment')?.value
      };

      this.saveCommentSubscription = this.httpService.saveEnquiryComment(this.enquiryId, commentData).subscribe({
        next: (response) => {
          this.closeCommentDrawer();
          // Refresh the comments list after successful save
          this.getComments();
        }
      });
    }
  }

  get checkEnquiryPage() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_PAGE);
  }

  get checkViewCommentPermission() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_VIEW_COMMENT);
  }

  get checkAddCommentPermission() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_ADD_COMMENT);
  }

  get checkEnquiryEdit() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_EDIT);
  }

  canEditEnquiry(): boolean {
    return this.checkEnquiryEdit && this.enquiryDetails?.platform === 'Direct';
  }

  editEnquiry(): void {
    this.router.navigate(['admin', 'enquiries', 'edit', this.enquiryId], {
      queryParams: this.listFilters
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.queryParamsSubscription?.unsubscribe();
    this.getEnquiryDetailsSubscription?.unsubscribe();
    this.getCommentsSubscription?.unsubscribe();
    this.saveCommentSubscription?.unsubscribe();
  }
}
